import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import * as Icons from "lucide-react";
import { LOGO_URL } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Booking() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ preferred_at: "", name: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/booking/${token}`)
      .then((r) => {
        setInfo(r.data);
        setForm((f) => ({ ...f, name: r.data.first_name || "", phone: r.data.phone || "" }));
        if (r.data.existing) setDone(true);
      })
      .catch(() => setNotFound(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.preferred_at) return;
    setBusy(true);
    try {
      await axios.post(`${API}/public/booking/${token}`, {
        preferred_at: form.preferred_at,
        name: form.name, phone: form.phone, note: form.note,
      });
      setDone(true);
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f1f5f4] p-6">
      <div className="text-center"><Icons.CalendarX size={40} className="mx-auto text-muted-foreground mb-3" />
        <p className="text-lg font-semibold">This booking link is no longer valid.</p>
        <p className="text-sm text-muted-foreground mt-1">Please contact Grian Solar Power directly.</p></div>
    </div>
  );

  if (!info) return <div className="min-h-screen flex items-center justify-center bg-[#f1f5f4]"><Icons.LoaderCircle className="animate-spin text-primary" /></div>;

  const phones = [info.business_phone, info.support_phone].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#f1f5f4] flex flex-col items-center py-10 px-4" data-testid="booking-page">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-center mb-6">
          <img src={LOGO_URL} alt="Grian Solar Power" className="h-14" />
        </div>

        <div className="bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.08)] overflow-hidden">
          <div className="bg-[#0B2A1A] text-white px-7 py-6">
            <h1 className="text-2xl font-head font-extrabold">Book your free consultation</h1>
            <p className="text-sm text-[#9fd8b6] mt-1">Pick a time that suits you — no obligation.</p>
            <div className="mt-3 inline-flex items-center gap-2 text-xs bg-white/10 rounded-full px-3 py-1.5">
              <Icons.Clock size={14} /> Available after-hours &amp; Saturdays
            </div>
          </div>

          {done ? (
            <div className="p-8 text-center" data-testid="booking-success">
              <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4"><Icons.Check size={28} /></div>
              <h2 className="text-xl font-head font-bold text-[#0B2A1A]">You&apos;re all set{info.first_name ? `, ${info.first_name}` : ""}!</h2>
              <p className="text-sm text-muted-foreground mt-2">Thanks for booking a callback. One of our solar specialists will call you at your preferred time.</p>
              {phones.length > 0 && (
                <p className="text-sm text-muted-foreground mt-4">Need us sooner? Call {phones.join(" or ")}.</p>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="p-7 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preferred date &amp; time · Melbourne</label>
                <input type="datetime-local" required value={form.preferred_at}
                  onChange={(e) => setForm({ ...form, preferred_at: e.target.value })} data-testid="booking-datetime"
                  className="w-full mt-1 px-3 py-2.5 rounded-lg border border-input bg-white focus:ring-2 focus:ring-primary focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your name</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name"
                    className="w-full mt-1 px-3 py-2.5 rounded-lg border border-input bg-white" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Phone</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone"
                    className="w-full mt-1 px-3 py-2.5 rounded-lg border border-input bg-white" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Anything you&apos;d like us to know? (optional)</label>
                <textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="booking-note"
                  className="w-full mt-1 px-3 py-2.5 rounded-lg border border-input bg-white" />
              </div>
              <button type="submit" disabled={busy} data-testid="booking-submit"
                className="w-full py-3 rounded-lg bg-[#0F7A3D] text-white font-bold hover:brightness-110 transition-all disabled:opacity-50">
                {busy ? "Booking…" : "Confirm my callback"}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-5">Grian Solar Power · ABN 91 635 362 560</p>
      </div>
    </div>
  );
}
